import { useState } from "react";
import { type Task, type Client } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { TaskTypeBadge } from "@/components/task-type-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star, Users } from "lucide-react";
import { statusLabel } from "@/shared/statusLabel";

interface TeamMember {
  id: number;
  invitedEmail: string;
  memberName?: string;
  role: string;
  status: string;
}

interface PersonalTaskViewProps {
  teamMembers: TeamMember[];
  tasks: Task[];
  clients: Client[];
  showTeamSelector?: boolean;
}

export function PersonalTaskView({ teamMembers, tasks, clients, showTeamSelector = true }: PersonalTaskViewProps) {
  const { user } = useAuth();
  const [selectedMemberId, setSelectedMemberId] = useState<string>("all");
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
  const safeClients = Array.isArray(clients) ? clients : [];

  const getStatusLabel = (status: unknown) => {
    try {
      return statusLabel(status as any, 'sk') || String(status || '-');
    } catch {
      return String(status || '-');
    }
  };

  // Filter tasks based on selected team member
  const filteredTasks = safeTasks.filter((task) => {
    if (!showTeamSelector) return true; // Ak nie je selector, zobraz všetky úlohy
    if (selectedMemberId === "all") return true;
    // Support both camelCase and snake_case
    const assignedTo = (task as any).assignedToId || (task as any).assigned_to_id;
    if (selectedMemberId === "unassigned") return !assignedTo;
    return assignedTo?.toString() === selectedMemberId;
  });

  const selectedMember = safeTeamMembers.find(m => m.id.toString() === selectedMemberId);

  return (
    <div className="space-y-4">
      {showTeamSelector && (
        <div className="flex items-center gap-4">
          <Users className="h-5 w-5 text-primary" />
          <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Vyberte člena tímu" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Všetci zamestnanci</SelectItem>
              <SelectItem value="unassigned">{(user as any)?.companyName || (user as any)?.businessName || 'Nepriradené úlohy'}</SelectItem>
              {safeTeamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id.toString()}>
                  {member.memberName || member.invitedEmail} ({member.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="bg-card border rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {!showTeamSelector 
              ? "Vaše úlohy"
              : selectedMemberId === "all" 
                ? "Všetky úlohy" 
                : selectedMemberId === "unassigned"
                  ? "Nepriradené úlohy"
                  : `Úlohy pre ${selectedMember?.memberName || selectedMember?.invitedEmail}`
            }
          </h3>
          <Badge variant="secondary">
            {filteredTasks.length} úloh
          </Badge>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {selectedMemberId === "unassigned" 
              ? "Žiadne nepriradené úlohy"
              : selectedMemberId === "all"
                ? "Žiadne úlohy"
                : "Tento zamestnanec nemá priradené žiadne úlohy"
            }
          </div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader className="bg-primary/5">
                <TableRow>
                  <TableHead className="font-bold text-sm">ID</TableHead>
                  <TableHead className="font-bold text-sm">Názov</TableHead>
                  <TableHead className="font-bold text-sm">Klient</TableHead>
                  <TableHead className="font-bold text-sm">Stav</TableHead>
                  <TableHead className="font-bold text-sm">Termín</TableHead>
                  <TableHead className="font-bold text-sm text-right">Hodiny</TableHead>
                  <TableHead className="font-bold text-sm text-right">Suma</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.map((task) => {
                  const clientId = (task as any).clientId || (task as any).client_id;
                  const client = safeClients.find((c) => c.id === clientId);
                  const taskType = (task as any).type || (task as any).task_type || "standard";
                  const hoursWorked = (task as any).hoursWorked ?? (task as any).hours_worked ?? (task as any).actualHours ?? 0;
                  const manualAmount = (task as any).manualAmount ?? (task as any).manual_amount;
                  const dueDate = (task as any).dueDate || (task as any).due_date;
                  
                  // Calculate total amount
                  let total = 0;
                  const taskWithTotal = task as any;
                  if (taskWithTotal.totalAmount !== undefined || taskWithTotal.total_amount !== undefined) {
                    total = taskWithTotal.totalAmount ?? taskWithTotal.total_amount;
                  } else {
                    if (taskType === "cash" || taskType === "offset") {
                      total = manualAmount ? parseFloat(manualAmount.toString()) : 0;
                      // Pre zápočty už máme záporné sumy v databáze, takže ich používame tak ako sú
                    } else {
                      total = parseFloat(hoursWorked.toString()) * (parseFloat(client?.hourlyRate?.toString() || "0"));
                    }
                  }

                  return (
                    <TableRow key={task.id} className="hover:bg-muted/30">
                      <TableCell className="text-muted-foreground font-mono text-sm">
                        {`250${task.id.toString().padStart(3, '0')}`}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {task.isStarred && (
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          )}
                          <TaskTypeBadge type={taskType} size="sm" />
                          <span>{task.title}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client?.businessName || client?.name}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            task.status === "completed"
                              ? "default"
                              : task.status === "in_progress"
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {getStatusLabel(task.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {dueDate
                          ? new Date(dueDate).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {hoursWorked}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {Number(total || 0).toFixed(2)}€
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}